import type { ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Card,
  Heading,
  BodyText,
  Button,
  Badge,
} from '@aaimplatform/ui';
import {
  AlertTriangle as WarningIcon,
  RefreshCw as RefreshIcon,
} from 'lucide-react';
import { useJurisdictionProfile } from '../hooks/useRegulatoryReadiness';
import { RouterBackLink } from '../components/RouterBackLink';
import { formatDate, formatMoney } from '../utils/display-format';
import { EnumText } from '../utils/status-labels';
import {
  ARTICLE12_STATUS_LABEL,
  ARTICLE8_VARIATION_LABEL,
  ARTICLE9_REVISION_LABEL,
  CONSUMER_PROTECTION_LABEL,
  DEBTOR_NAMING_RULE_LABEL,
  GLBA_EXEMPTION_LABEL,
  NOT_RECORDED_LABEL,
  PRIVACY_REGIME_LABEL,
  REGULATORY_CORRECTIONS_EMAIL,
  REGULATORY_ENTITY_PARAM,
  REGULATORY_PROVENANCE_NOTE,
  jurisdictionLabel,
  jurisdictionSubtitle,
} from '../lib/regulatory-labels';

/**
 * A calendar day. article12EffectiveDate, article12AdjustmentDate and
 * lastVerified are days stored at UTC midnight (jurisdiction-seed.ts writes
 * `new Date('2024-07-01')`), so they are formatted in UTC. Formatted in the
 * viewer's zone, every US viewer saw the previous day.
 */
function formatCalendarDay(value: string | null | undefined): string {
  return value ? formatDate(value, { timeZone: 'UTC' }) : NOT_RECORDED_LABEL;
}

/**
 * A profile field drawn from a label map. An absent value prints "Not
 * recorded"; an unrecognized one prints "Unrecognized" with the code in the
 * title attribute.
 */
function LabeledValue<K extends string>({
  map,
  value,
}: {
  map: Record<K, string>;
  value: string | null | undefined;
}) {
  if (value === null || value === undefined || value === '') {
    return <>{NOT_RECORDED_LABEL}</>;
  }
  return <EnumText map={map} value={value} />;
}

function ProfileRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between items-center gap-4">
      <BodyText variant="ui" className="text-sm text-tertiary">{label}</BodyText>
      <BodyText variant="ui" className="text-sm font-medium text-right">{children}</BodyText>
    </div>
  );
}

function CardTitle({ children }: { children: ReactNode }) {
  return (
    <Heading level={3} size="card" className="mb-3">
      {children}
    </Heading>
  );
}

export function JurisdictionDetailPage() {
  const { jurisdictionCode } = useParams<{ jurisdictionCode: string }>();
  const [searchParams] = useSearchParams();
  const { data: profile, isLoading, error, refetch } = useJurisdictionProfile(jurisdictionCode);

  // Return to the heat map with the institution it was opened from still
  // selected, rather than the empty selector.
  const entityId = searchParams.get(REGULATORY_ENTITY_PARAM);
  const backTo = entityId
    ? `/regulatory?${REGULATORY_ENTITY_PARAM}=${encodeURIComponent(entityId)}`
    : '/regulatory';

  const backLink = (
    <RouterBackLink to={backTo}>Back to Regulatory Readiness</RouterBackLink>
  );

  if (error) {
    return (
      <div>
        {backLink}
        <Card className="p-6 text-center">
          <WarningIcon className="text-error mb-2 mx-auto" size={32} />
          <BodyText variant="ui" className="text-error mb-4">
            Failed to load jurisdiction profile
          </BodyText>
          <BodyText variant="ui" className="text-tertiary text-sm mb-4">
            {error.message}
          </BodyText>
          <Button variant="primary" onClick={() => refetch()}>
            <RefreshIcon className="mr-2" size={20} />
            Retry
          </Button>
        </Card>
      </div>
    );
  }

  if (isLoading || !profile) {
    return (
      <div>
        {backLink}
        <Card className="p-6 text-center">
          <BodyText variant="ui" className="text-tertiary">
            Loading jurisdiction profile...
          </BodyText>
        </Card>
      </div>
    );
  }

  return (
    <div>
      {backLink}

      {/* Header. The layout's header bar is the page h1, so the jurisdiction
          name is the first h2 beneath it. JurisdictionProfile.name is
          spec-required. */}
      <div className="mb-6">
        <Heading level={2}>{profile.name}</Heading>
        <BodyText variant="ui" className="text-tertiary mt-1">
          {jurisdictionSubtitle(profile.jurisdictionCode, profile.uccTitleReference)}
        </BodyText>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* UCC Framework. Article 12 status describes the jurisdiction and
            signals nothing to act on, so every status renders neutral; red is
            reserved for CTAs and alerts. */}
        <Card className="p-4">
          <CardTitle>UCC Framework</CardTitle>
          <div className="space-y-2">
            <ProfileRow label="Article 8 Variation">
              <LabeledValue map={ARTICLE8_VARIATION_LABEL} value={profile.article8Variation} />
            </ProfileRow>
            <ProfileRow label="Article 9 Revision">
              <LabeledValue map={ARTICLE9_REVISION_LABEL} value={profile.article9Revision} />
            </ProfileRow>
            <div className="flex justify-between items-center gap-4">
              <BodyText variant="ui" className="text-sm text-tertiary">Article 12 Status</BodyText>
              <Badge variant="neutral" size="small">
                {jurisdictionLabel(ARTICLE12_STATUS_LABEL, profile.article12Status)}
              </Badge>
            </div>
            {profile.article12EffectiveDate && (
              <ProfileRow label="Article 12 Effective">
                {formatCalendarDay(profile.article12EffectiveDate)}
              </ProfileRow>
            )}
            {profile.article12AdjustmentDate && (
              <ProfileRow label="Article 12 Adjustment">
                {formatCalendarDay(profile.article12AdjustmentDate)}
              </ProfileRow>
            )}
          </div>
        </Card>

        {/* Filing Information. E-filing availability is an attribute, so it
            renders neutral either way. */}
        <Card className="p-4">
          <CardTitle>Filing Information</CardTitle>
          <div className="space-y-2">
            <ProfileRow label="Filing Office">{profile.filingOffice}</ProfileRow>
            <ProfileRow label="UCC-1 Fee">{formatMoney(profile.ucc1Fee)}</ProfileRow>
            <div className="flex justify-between items-center gap-4">
              <BodyText variant="ui" className="text-sm text-tertiary">E-Filing</BodyText>
              <Badge variant="neutral" size="small">
                {profile.eFilingAvailable ? 'Available' : 'Not available'}
              </Badge>
            </div>
            <ProfileRow label="Debtor Naming Rule">
              <LabeledValue map={DEBTOR_NAMING_RULE_LABEL} value={profile.debtorNamingRule} />
            </ProfileRow>
          </div>
        </Card>

        {/* Consumer Protection. Intensity is a jurisdiction attribute, not an
            alert, so every level renders neutral; red is reserved for CTAs
            and alerts. */}
        <Card className="p-4">
          <CardTitle>Consumer Protection</CardTitle>
          <div className="space-y-2">
            <div className="flex justify-between items-center gap-4">
              <BodyText variant="ui" className="text-sm text-tertiary">Intensity Level</BodyText>
              <Badge variant="neutral" size="small">
                {jurisdictionLabel(CONSUMER_PROTECTION_LABEL, profile.consumerProtectionIntensity)}
              </Badge>
            </div>
          </div>
        </Card>

        {/* Privacy */}
        <Card className="p-4">
          <CardTitle>Privacy</CardTitle>
          <div className="space-y-2">
            <ProfileRow label="Regime">
              <LabeledValue map={PRIVACY_REGIME_LABEL} value={profile.privacyRegime} />
            </ProfileRow>
            <ProfileRow label="GLBA Exemption">
              <LabeledValue map={GLBA_EXEMPTION_LABEL} value={profile.privacyGlbaExemptionType} />
            </ProfileRow>
          </div>
        </Card>

        {/* Trust Company (conditional) */}
        {(profile.trustCompanyRegulator || profile.trustCompanyCharterType) && (
          <Card className="p-4">
            <CardTitle>Trust Company</CardTitle>
            <div className="space-y-2">
              {profile.trustCompanyRegulator && (
                <ProfileRow label="Regulator">{profile.trustCompanyRegulator}</ProfileRow>
              )}
              {profile.trustCompanyCharterType && (
                <ProfileRow label="Charter Type">{profile.trustCompanyCharterType}</ProfileRow>
              )}
            </div>
          </Card>
        )}

        {/* Verification */}
        <Card className="p-4">
          <CardTitle>Verification</CardTitle>
          <div className="space-y-2">
            <ProfileRow label="Last Verified">{formatCalendarDay(profile.lastVerified)}</ProfileRow>
            <div>
              <BodyText variant="ui" className="text-sm text-tertiary mb-1">Sources</BodyText>
              <div className="flex flex-wrap gap-1">
                {profile.verificationSources.map((source: string) => (
                  <Badge key={source} variant="neutral" size="small">
                    {source}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Data provenance footer, shared with the heat map page. */}
      <div className="mt-8 pt-4 border-t border-recessed">
        <BodyText variant="ui" className="text-xs text-tertiary">
          {REGULATORY_PROVENANCE_NOTE}{' '}
          <a
            href={`mailto:${REGULATORY_CORRECTIONS_EMAIL}`}
            className="text-secondary underline underline-offset-2 hover:text-primary"
          >
            {REGULATORY_CORRECTIONS_EMAIL}
          </a>
          .
        </BodyText>
      </div>
    </div>
  );
}
